import { action, makeObservable, observable, runInAction } from "mobx";
import api from "../../api";
import Manufacturer, { InventoryValue } from "./Manufacturer";

class ManufacturerStore {
  manufacturer?: Manufacturer | null = undefined;
  inventoryValue?: InventoryValue[] = undefined;

  constructor() {
    makeObservable(this, {
      manufacturer: observable,
      inventoryValue: observable,
      getManufacturerInfo: action.bound,
      updateManufacturerProperty: action.bound,
      getInventoryValue: action.bound,
      downloadReport: action.bound,
      downloadSales: action.bound,
      downloadInventory: action.bound,
      downloadClaim: action.bound,
      sendManufacturerFeedback: action.bound
    });
  }

  async getManufacturerInfo(applicationId: string) {
    try {
      const info = await api.manufacturers.retrieveInfo(applicationId);
      const manufacturer = Manufacturer.convertFrom(info);

      runInAction(() => {
        this.manufacturer = manufacturer;
        document.title = (manufacturer.publicName || manufacturer.name) + " | Randmar Inc.";
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.manufacturer = null;
      });
    }
  }

  async updateManufacturerProperty(applicationId: string, fieldId: string, newValue: string): Promise<boolean> {
    try {
      return await api.manufacturers.updateManufacturerProperty(
        applicationId,
        fieldId,
        newValue
      );
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getInventoryValue(applicationId: string) {
    try {
      const res = await api.manufacturers.retrieveInventoryValue(applicationId);

      const inventory = res.map(InventoryValue.convertFrom);
      runInAction(() => {
        this.inventoryValue = inventory;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async downloadReport(applicationId: string, type: string, email: string): Promise<boolean> {
    try {
      return await api.manufacturers.downloadReport(applicationId, type, email);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async downloadSales(applicationId: string, email: string, dateStart: string, dateEnd: string, resellerId?: string): Promise<boolean> {
    try {
      if (resellerId)
        return await api.manufacturers.downloadResellerSales(applicationId, resellerId, email, dateStart, dateEnd);

      return await api.manufacturers.downloadSales(applicationId, email, dateStart, dateEnd);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async downloadClaim(applicationId: string, email: string, dateStart: string, dateEnd: string): Promise<boolean> {
    try {
      return await api.manufacturers.downloadClaim(applicationId, email, dateStart, dateEnd);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async downloadInventory(applicationId: string, email: string, inventoryDate: string): Promise<boolean> {
    try {
      return await api.manufacturers.downloadInventory(applicationId, email, inventoryDate);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async sendManufacturerFeedback(applicationId: string, emailAddress: string, comment: string): Promise<boolean> {
    try {
      return await api.manufacturers.sendManufacturerFeedback(applicationId, emailAddress, comment);
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}

export default ManufacturerStore;
